// Contains util method to handle i3dx URI
import type { CommunityType } from '~/entities/container/community/interfaces/community'

import { getContentModelFromSubjectUri } from '~/features-shared/content/api/parser-contents'
import { I3dxDomain } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

/**
 * Defines the data that is extracted from i3dx uri.
 */
interface I3dxRegExpResult {
  /** Id of the resource. */
  objectId: string
  /** Domain name of which the resource is part of */
  domainName: I3dxDomain
}
/**
 * Defines data that is extracted from i3dx uri.
 */
interface I3DXExtractedData {
  /** Id of the resource. */
  objectId: string
  /**
   * Type of the object that is has been extracted from i3dx string.
   * This could be undefined because domain name in the i3dx string may not have a valid
   * corresponding type in swym.
   */
  objectType?: CommunityType | IContentModel | undefined
}

/**
 * Mapping i3dx domain name to it's corresponding entity name in swym.
 */
const i3DXDomainObjectTypeMap = new Map<I3dxDomain, CommunityType | IContentModel>([
  [I3dxDomain.COMMUNITY, 'community'],
  [I3dxDomain.POST, IContentModel.Post],
  [I3dxDomain.IDEA, IContentModel.Idea],
  [I3dxDomain.MEDIA, IContentModel.Media],
  [I3dxDomain.QUESTION, IContentModel.Question],
  [I3dxDomain.WIKI, IContentModel.Wiki],
])

/**
 * Mapping Swym entity name to it's corresponding i3dx domain name
 */
const i3dXDomainNameMapping = new Map<IContentModel | CommunityType, I3dxDomain>([
  [IContentModel.Post, I3dxDomain.POST],
  [IContentModel.Media, I3dxDomain.MEDIA],
  [IContentModel.Question, I3dxDomain.QUESTION],
  [IContentModel.Wiki, I3dxDomain.WIKI],
  [IContentModel.Idea, I3dxDomain.IDEA],
  ['community', I3dxDomain.COMMUNITY],
])

/**
 * Regex to extract domain name and resource Id from i3dx uri string.
 * domainName: It is the name of the domain. Example: 'Community' | 'Post' | RichMedia.. etc
 * objectId: 3DSwym Subject URI
 * @example "i3dx:<domainName>!<objectId>"
 */
export const I3DX_REGEX = /i3dx:(?<domainName>[^]*)!(?<objectId>[^]*)/i

/**
 * Get object type name corresponding to i3dx domain name
 * @param domainName i3dx Domain name
 * @returns Object type name
 */
function getObjectTypefromI3dxDomainName(domainName: I3dxDomain): IContentModel | CommunityType | undefined {
  return i3DXDomainObjectTypeMap.get(domainName)
}

/**
 * Get i3Dx domain name for corresponding item.
 * @param item Element that needs equivalent i3dx domain name.
 * @returns i3dx domain name
 */
function getI3dxDomainName(item: IContentModel | CommunityType): I3dxDomain | undefined {
  return i3dXDomainNameMapping.get(item)
}

/**
 * Get i3dx uri string.
 * @param subjectUri Subject URI of the element
 * @returns i3dx uri string
 * @example i3dx uri string structure : "i3dx:<domainName>!<objectId>"
 */
export function getI3dxUri(subjectUri: SUBJECT_6W_URI): string {
  const contentType = getContentModelFromSubjectUri(subjectUri)
  let domainName

  // If it is not a valid content type, then it might be a 'community' or any other type.
  if (!contentType) {
    const [type] = subjectUri.split(':')
      .splice(-2, 1)
    if (type && type === 'community')
      domainName = getI3dxDomainName(type)
    if (domainName)
      return `i3dx:${domainName}!${subjectUri}`
    return ''
  }
  domainName = getI3dxDomainName(contentType)
  if (domainName)
    return `i3dx:${domainName}!${subjectUri}`
  return ''
}

/**
 * Extract data from i3dx uri string.
 * @param i3DxUri
 * @returns data extracted from i3dx uri
 */
export function extractDataFromi3DxUri(i3DxUri: string): I3DXExtractedData {
  const { objectId, domainName } = (i3DxUri.match(I3DX_REGEX)?.groups || {}) as unknown as I3dxRegExpResult
  return {
    objectId,
    objectType: getObjectTypefromI3dxDomainName(domainName),
  }
}
